import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'
import PropTypes from 'prop-types'
import { injectScript } from '@shared/utils'
import ReelProgress from '../reelProgress'

// Create a promise to track YouTube API loading
let youtubePromise = null

function loadYouTubeAPI() {
  if (!youtubePromise) {
    youtubePromise = new Promise((resolve) => {
      if (window?.YT?.loaded === 1) {
        resolve()
      } else {
        // Set the callback before injecting the script
        window.onYouTubeIframeAPIReady = () => {
          resolve()
        }
        injectScript('https://www.youtube.com/iframe_api')
      }
    })
  }
  return youtubePromise
}

const ReelYoutube = forwardRef(({ id, isCurrent, onPlayerReady, styles, isPreview }, ref) => {
  // const [playerStats, setPlayerStats] = useState(-1)
  const player = useRef()
  const totalDuration = useRef(0)
  const range = useRef()
  const interval = useRef()
  const overlay = useRef()

  useImperativeHandle(ref, () => ({
    mute: () => playerEvents('mute'),
    unMute: () => playerEvents('unMute'),
    play: () => playerEvents('playVideo'),
    pause: () => playerEvents('pauseVideo'),
    getPlayerState: () => playerEvents('getPlayerState'),
    getCurrentTime: () => playerEvents('getCurrentTime'),
    isPushed: () => false,
    isMuted: () => {
      return playerEvents('isMuted')
    }
  }))

  function onReady(videoId) {
    player.current = new window.YT.Player(`p-${videoId}`, {
      height: '100%',
      width: '100%',
      videoId,
      playerVars: {
        playsinline: 1,
        autoplay: isCurrent ? 1 : 0,
        controls: 0,
        disablekb: 1,
        mute: 1,
        loop: 1,
        rel: 0,
        fs: 0,
        iv_load_policy: 3,
        endscreen: 0,
        related: 0,
        showinfo: 1
        // playlist: videoId
      },
      events: {
        onReady: (event) => {
          totalDuration.current = Math.round(playerEvents('getDuration'))
          handleChangeSeconds()
          onPlayerReady()
        },
        onStateChange: ({ data }) => {
          // setPlayerStats(data)
          if (data === 1) startListening()
          // else if (data === 2) playerEvents('playVideo')
          else {
            if (data === 0) {
              playerEvents('playVideo')
            }
          }
        }
      }
    })
  }

  function playerEvents(name, params) {
    if (typeof player.current?.[name] === 'function') {
      return params ? player.current[name](params) : player.current[name]()
    } else return null
  }

  function handleChangeSeconds() {
    const t = Math.round((playerEvents('getCurrentTime') / totalDuration.current) * 100)
    if (t !== null && range.current) {
      range.current.value = t
    }

    if (t >= 3 && overlay.current?.style) {
      overlay.current.style.opacity = 0
    }
  }

  function startListening() {
    let rafId = null

    function update() {
      handleChangeSeconds()
      rafId = requestAnimationFrame(update)
    }

    // Clear previous RAF if any
    if (interval.current) {
      cancelAnimationFrame(interval.current)
    }

    rafId = requestAnimationFrame(update)
    interval.current = rafId

    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
    }
  }

  useEffect(() => {
    loadYouTubeAPI().then(() => onReady(id))
    return () => {
      if (player.current) {
        playerEvents('destroy') // Destroy YouTube player
        player.current = null
      }
      if (interval.current) {
        clearInterval(interval.current)
        interval.current = null
      }
    }
  }, [id])

  function handleSeek(t) {
    const newTime = (t / 100) * totalDuration.current
    playerEvents('seekTo', newTime)
  }

  return (
    <>
      <div ref={overlay} className={`position-absolute top-0 start-0 w-100 ${styles.overLay}`} />
      <div id={`p-${id}`} className="h-100 pe-none" />
      {!isPreview && <ReelProgress ref={range} handleSeek={handleSeek} />}
    </>
  )
})
ReelYoutube.displayName = 'ReelYoutube'
export default ReelYoutube
ReelYoutube.propTypes = {
  id: PropTypes.string.isRequired,
  isCurrent: PropTypes.bool.isRequired,
  onPlayerReady: PropTypes.func.isRequired,
  styles: PropTypes.object.isRequired,
  isPreview: PropTypes.bool
}
